/* eslint-disable react-hooks/refs */
import type { MouseEvent, ReactNode } from 'react';
import type {
  ButtonModalProps,
  ModalPanelProps,
} from '../components/Modal/ModalPanel/ModalPanel';

import {
  createContext,
  use,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment';

import Loader from '../components/Loader/Loader';
import Modal from '../components/Modal/Modal';
import ModalPanel from '../components/Modal/ModalPanel/ModalPanel';

import {
  CheckmarkIcon,
  CloseIcon,
  FloppyDiskIcon,
  TrashIcon,
  WarningIcon,
  WarningTriangleRedIcon,
} from '../images/shapes';

import i18next from '../i18n';

type ModalInstanceButtonProps = {
  /**
   * Result to return after button click
   */
  result?: any;
} & ButtonModalProps;

export type ModalUserConfigProps = {
  /** Unique modal ID. If not provided, custom id will be generated */
  id?: string | number;
  /** Modal buttons */
  buttons?: ModalInstanceButtonProps[];
} & Pick<
  ModalPanelProps,
  | 'title'
  | 'content'
  | 'size'
  | 'hideClose'
  | 'dialogAdditionalClasses'
  | 'contentAdditionalClasses'
  | 'testId'
>;

interface ModalConfigProps extends ModalUserConfigProps {
  id: string | number;
}

interface ModalPromiseProps extends ModalConfigProps {
  promise: Promise<any>;
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}

interface ModalInstanceProps {
  modalConfig: ModalConfigProps;
  onClose: (
    modalConfig: ModalConfigProps,
    e?: MouseEvent<HTMLButtonElement>,
    button?: ModalInstanceButtonProps,
  ) => any;
}

export type ModalContextFn = ((
  modalConfig: ModalUserConfigProps,
) => Promise<any>) & {
  isOpen: (id: string) => boolean;
  delete: (
    content: ReactNode,
    id?: string,
    onDeleteClick?: (e?: MouseEvent<HTMLButtonElement>) => any,
    onCancelClick?: (e?: MouseEvent<HTMLButtonElement>) => any,
    options?: ModalUserConfigProps,
  ) => Promise<boolean>;
  deleting: (id: string, options?: ModalUserConfigProps) => Promise<any>;
  info: (
    text: ReactNode,
    content: ReactNode,
    id?: string,
    options?: ModalUserConfigProps,
  ) => Promise<any>;
  confirmation: (
    content: ReactNode,
    text?: string,
    trueLabel?: string,
    falseLabel?: string,
    type?: TextRenderOptions,
    options?: ModalUserConfigProps,
  ) => Promise<any>;
  close: (id: string | number, result?: any) => void;
};

let modalFnInstance: ModalContextFn;
export const getModal = () => modalFnInstance;

export const emptyModalContextValue: ModalContextFn = () => Promise.resolve();
emptyModalContextValue.isOpen = () => false;
emptyModalContextValue.delete = () => Promise.resolve(false);
emptyModalContextValue.deleting = () => Promise.resolve(false);
emptyModalContextValue.info = () => Promise.resolve(false);
emptyModalContextValue.confirmation = () => Promise.resolve(false);
emptyModalContextValue.close = () => {};

export const ModalContext = createContext<ModalContextFn>(
  emptyModalContextValue,
);

/**
 * Single modal instance context
 */
export type ModalInstance = {
  /** Function to close a modal and resolve its promise */
  resolve?: (value?: any) => void;
};
export const ModalInstanceContext = createContext<ModalInstance>({});

type TextRenderOptions = 'warning' | 'info' | 'simple';

export const defaultTextRender = {
  warning: (text: ReactNode) => (
    <div className="inline-flex text-red font-bold text-3xl items-center">
      <WarningTriangleRedIcon className="h-5 mr-2.5" />
      {text}
    </div>
  ),
  info: (text: ReactNode) => (
    <div className="inline-flex text-blue font-bold text-3xl items-center">
      <WarningIcon className="h-5 mr-2.5" />
      {text}
    </div>
  ),
  simple: (text: ReactNode) => (
    <div className="inline-flex font-bold text-3xl items-center">{text}</div>
  ),
};

export const defaultButtonsProps = {
  cancel: {
    key: 'cancel',
    color: 'blueBordered' as const,
    iconPosition: 'start' as const,
    iconImage: <CloseIcon className="w-3 min-w-3" />,
  } as ModalInstanceButtonProps,
  save: {
    key: 'save',
    color: 'blue' as const,
    iconPosition: 'start' as const,
    iconImage: <FloppyDiskIcon className="w-3 min-w-3" />,
  },
  ok: {
    key: 'ok',
    color: 'blue' as const,
    iconPosition: 'start' as const,
    iconImage: <CheckmarkIcon className="w-3 min-w-3" />,
  } as ModalInstanceButtonProps,
  delete: {
    key: 'delete',
    color: 'red' as const,
    iconPosition: 'start' as const,
    iconImage: <TrashIcon className="w-3 min-w-3" />,
  } as ModalInstanceButtonProps,
  deleting: {
    key: 'deleting',
    color: 'red' as const,
    iconPosition: 'start' as const,
    iconImage: <Loader size="small" type="spinner-grid" />,
  } as ModalInstanceButtonProps,
};

const ModalInstance = ({ modalConfig, onClose }: ModalInstanceProps) => {
  const finalButtons = useMemo(
    () =>
      (modalConfig.buttons || []).map((button: ModalInstanceButtonProps) => ({
        ...button,
        onClick: (e?: MouseEvent<HTMLButtonElement>) =>
          onClose(modalConfig, e, button),
      })),
    [modalConfig, onClose],
  );

  return (
    <ModalPanel
      onClose={(e: MouseEvent<HTMLButtonElement>) => onClose(modalConfig, e)}
      buttons={finalButtons}
      title={modalConfig.title}
      content={modalConfig.content}
      size={modalConfig.size}
      hideClose={modalConfig.hideClose}
      dialogAdditionalClasses={modalConfig.dialogAdditionalClasses}
      contentAdditionalClasses={modalConfig.contentAdditionalClasses}
      testId={modalConfig.testId}
    />
  );
};

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modals, setModals] = useState<{ [key: string]: ModalConfigProps }>({});
  const modalPromises = useRef<{ [key: string]: ModalPromiseProps }>({});

  const cleanupModal = useCallback(async (id: string | number) => {
    setModals((oldModals) => {
      const newModals = { ...oldModals };
      delete newModals[id];
      return newModals;
    });
    delete modalPromises.current[id];
  }, []);

  const resolveModal = useCallback(
    async (id: string | number, result: any) => {
      if (modalPromises.current[id]) modalPromises.current[id].resolve(result);
      await cleanupModal(id);
    },
    [cleanupModal],
  );

  const modal = useMemo(() => {
    const memoFn = (options: ModalUserConfigProps) => {
      const {
        id = moment.now(),
        buttons = [],
        title = '',
        content = '',
        size = '',
        hideClose = false,
        dialogAdditionalClasses = '',
        contentAdditionalClasses = '',
        testId = '',
      } = options;

      const modalPromiseData = modalPromises.current[id] || {};

      if (!modalPromiseData.promise) {
        // We are modyfing ref value, so is safe to disable lint rule
        // eslint-disable-next-line react-hooks/immutability
        modalPromiseData.promise = new Promise((resolve, reject) => {
          modalPromiseData.resolve = resolve;
          modalPromiseData.reject = reject;
        });

        modalPromises.current[id] = modalPromiseData;
      }

      setModals((oldModals) => ({
        ...oldModals,
        [id]: {
          id,
          buttons: buttons.length ? buttons : oldModals[id]?.buttons,
          title: title || oldModals[id]?.title,
          content: content || oldModals[id]?.content,
          size: size || oldModals[id]?.size,
          hideClose: hideClose || oldModals[id]?.hideClose,
          dialogAdditionalClasses:
            dialogAdditionalClasses || oldModals[id]?.dialogAdditionalClasses,
          contentAdditionalClasses:
            contentAdditionalClasses || oldModals[id]?.contentAdditionalClasses,
          testId: testId || oldModals[id]?.testId,
        },
      }));

      return modalPromiseData.promise;
    };

    memoFn.isOpen = (id: string | number) => !!modalPromises.current[id];
    memoFn.close = (id: string | number, result = null) =>
      resolveModal(id, result);

    memoFn.confirmation = (
      content: ReactNode,
      text = '',
      trueLabel = '',
      falseLabel = '',
      type: TextRenderOptions = 'warning',
      options = {},
    ) =>
      memoFn({
        title: defaultTextRender[type](text || i18next.t('Global.Warning')),
        content,
        buttons: [
          {
            ...defaultButtonsProps.ok,
            label: trueLabel || i18next.t('Global.Ok'),
            result: true,
          },
          {
            ...defaultButtonsProps.cancel,
            label: falseLabel || i18next.t('Global.Cancel'),
            result: false,
          },
        ],
        ...options,
      });

    memoFn.delete = (
      content: ReactNode,
      id = '',
      onDeleteClick = () => true,
      onCancelClick = () => false,
      options = {},
    ) =>
      memoFn({
        id,
        title: defaultTextRender.warning(i18next.t('Global.Warning')),
        content,
        buttons: [
          {
            ...defaultButtonsProps.delete,
            label: i18next.t('Global.Delete'),
            onClick: onDeleteClick,
          },
          {
            ...defaultButtonsProps.cancel,
            label: i18next.t('Global.Cancel'),
            onClick: onCancelClick,
          },
        ],
        ...options,
      });

    memoFn.deleting = (id: string, options = {}) =>
      memoFn({
        id,
        buttons: [
          {
            ...defaultButtonsProps.deleting,
            label: i18next.t('Global.Delete'),
            disabled: true,
          },
          {
            ...defaultButtonsProps.cancel,
            label: i18next.t('Global.Cancel'),
            disabled: true,
          },
        ],
        hideClose: true,
        ...options,
      });

    memoFn.info = (
      text: ReactNode,
      content: ReactNode,
      id = '',
      options = {},
    ) =>
      memoFn({
        id,
        title: defaultTextRender.info(text),
        content,
        buttons: [
          {
            ...defaultButtonsProps.ok,
            label: i18next.t('Global.Ok'),
            result: true,
          },
        ],
        ...options,
      });

    // eslint-disable-next-line react-hooks/globals
    modalFnInstance = memoFn;
    return memoFn;
  }, [resolveModal]);

  const onClose = useCallback(
    async (
      modalConfig: ModalConfigProps,
      event?: any,
      button?: ModalInstanceButtonProps,
    ) => {
      try {
        const buttonResult = button ? button.result : null;
        const result = button?.onClick ? button.onClick(event) : buttonResult;
        await resolveModal(modalConfig.id, result);
      } catch (e) {
        modalPromises.current[modalConfig.id].reject(e);
        await cleanupModal(modalConfig.id);
      }
    },
    [resolveModal, cleanupModal],
  );

  const modalProviderValue = useCallback(
    (modalConfig: ModalConfigProps) => ({
      resolve: (value: any) => resolveModal(modalConfig.id, value),
      promise: modalPromises.current[modalConfig.id]?.promise,
    }),
    [resolveModal],
  );

  return (
    <ModalContext value={modal}>
      {children}
      <Modal open={Object.values(modals).length > 0}>
        {Object.values(modals).map((modalConfig) => (
          <ModalInstanceContext
            key={modalConfig.id}
            value={modalProviderValue(modalConfig)}
          >
            <ModalInstance modalConfig={modalConfig} onClose={onClose} />
          </ModalInstanceContext>
        ))}
      </Modal>
    </ModalContext>
  );
};

export const useModals = () => use(ModalContext);
